/**
 * The +/-1% acceptance check.
 *
 * Spec, "Computed fields and acceptance rules":
 *   Computer vs actual | (check_counts x wt_per_count - actual_kg) / actual_kg | CSA A23.1 +/-1% -> pass/fail
 *   Water check        | water_litres / water_kg                              | +/-1% -> pass/fail
 *
 * Both are the same test: a measured value against a reference, judged on the
 * signed relative deviation. This module is the one place that decides
 * pass/fail so the two checks can never drift apart.
 */

import { TOLERANCE_FRACTION, type ToleranceCheck, type Verdict } from '../types';
import { toFinite, type Numeric } from './numbers';

/**
 * Slack allowed on the inclusive boundary, to absorb binary floating-point
 * noise only. Without it (1.01 - 1) / 1 evaluates to 0.010000000000000009 and
 * an exactly-1% deviation would read as a failure.
 *
 * This is NOT a widening of the acceptance range: 1.0001% still fails.
 */
export const TOLERANCE_EPSILON = 1e-12;

/**
 * A `ToleranceCheck` (the wire shape in ../types) plus the raw ratio, which is
 * what the calibration worksheet actually prints next to the water check.
 */
export interface ToleranceResult extends ToleranceCheck {
  /** value / reference. Exactly 1 is a perfect match; 1.00692 is +0.692%. */
  ratio: number;
}

/**
 * Judge `value` against `reference` at +/-`tolerance` (default +/-1%).
 *
 * Returns `null` when either side is missing or the reference is zero — an
 * unanswered check is not a failed check, and a QA record must not show a
 * verdict it did not compute.
 *
 * The boundary is inclusive: exactly +/-1% passes.
 */
export function toleranceCheck(
  value: Numeric,
  reference: Numeric,
  tolerance: number = TOLERANCE_FRACTION
): ToleranceResult | null {
  const v = toFinite(value);
  const r = toFinite(reference);
  if (v === null || r === null || r === 0) return null;

  const ratio = v / r;
  const deviation = (v - r) / r;
  if (!Number.isFinite(ratio) || !Number.isFinite(deviation)) return null;

  const limit = Math.abs(tolerance) + TOLERANCE_EPSILON;
  const verdict: Verdict = Math.abs(deviation) <= limit ? 'pass' : 'fail';

  return {
    value: v,
    reference: r,
    ratio,
    deviation,
    deviation_percent: Math.abs(deviation) * 100,
    verdict
  };
}
